// @ts-nocheck
"use client"

import { useState, useEffect } from "react"
import { OptimizedImage } from "@/components/ui/OptimizedImage"
import { cn } from "@/lib/utils"
import type { ImageObject } from "@/lib/content-data"
import useEmblaCarousel from "@/components/ui/use-embla-carousel"
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

interface ProductGalleryProps {
  images: ImageObject[]
  productName: string
}

export function ProductGallery({ images, productName }: ProductGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalIndex, setModalIndex] = useState(0)

  // Main carousel
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      axis: 'x',
    },
    [WheelGesturesPlugin()]
  )

  // Thumbnail carousel
  const [emblaThumbnailRef, emblaThumbnailApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    axis: 'x',
  })

  // Modal carousel
  const [emblaModalRef, emblaModalApi] = useEmblaCarousel(
    {
      loop: true,
      axis: 'x',
    },
    [WheelGesturesPlugin()]
  )

  // Sync main carousel with selected index
  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  // Sync modal carousel with modal index
  useEffect(() => {
    if (!emblaModalApi) return

    const onSelect = () => {
      setModalIndex(emblaModalApi.selectedScrollSnap())
    }

    emblaModalApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaModalApi.off('select', onSelect)
    }
  }, [emblaModalApi])

  // Sync thumbnail carousel with main carousel
  useEffect(() => {
    if (!emblaThumbnailApi || !emblaApi) return

    const scrollToThumbnail = () => {
      const selected = emblaApi.selectedScrollSnap()
      emblaThumbnailApi.scrollTo(selected)
    }

    emblaApi.on('select', scrollToThumbnail)
    scrollToThumbnail()

    return () => {
      emblaApi.off('select', scrollToThumbnail)
    }
  }, [emblaApi, emblaThumbnailApi])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isModalOpen])

  if (!images || images.length === 0) {
    return (
      <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center text-gray-400">
        No images available
      </div>
    )
  }

  const currentImage = images[selectedIndex]
  const imageUrl = currentImage?.variants?.desktop?.url || currentImage?.variants?.tablet?.url || currentImage?.url
  const focalPoint = currentImage?.cropFocalPoint || { x: 50, y: 50 }

  const goToPrevious = () => {
    emblaApi?.scrollPrev()
  }

  const goToNext = () => {
    emblaApi?.scrollNext()
  }

  const openModal = (index: number) => {
    setModalIndex(index)
    setIsModalOpen(true)
    // Scroll modal carousel to the selected index
    setTimeout(() => {
      emblaModalApi?.scrollTo(index)
    }, 0)
  }

  const goToNextModal = () => {
    emblaModalApi?.scrollNext()
  }

  const goToPreviousModal = () => {
    emblaModalApi?.scrollPrev()
  }

  const onThumbnailClick = (index: number) => {
    emblaApi?.scrollTo(index)
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Main Image Carousel */}
        <div className="relative group">
          <div className="overflow-hidden md:rounded-[2rem]" ref={emblaRef}>
            <div className="flex">
              {images.map((image, index) => {
                const imgUrl = image?.variants?.desktop?.url || image?.variants?.tablet?.url || image?.url
                const imgFocalPoint = image?.cropFocalPoint || { x: 50, y: 50 }

                return (
                  <div
                    key={index}
                    className="flex-[0_0_100%] min-w-0"
                  >
                    <div
                      className="relative w-full aspect-[1312/1000] bg-brand-cream-light cursor-pointer overflow-hidden"
                      onClick={() => openModal(index)}
                    >
                      <OptimizedImage
                        image={image}
                        alt={image.altText || productName}
                        sizes="(max-width: 768px) 100vw, 60vw"
                        className="object-cover absolute inset-0 w-full h-full"
                        objectPosition={`${imgFocalPoint.x}% ${imgFocalPoint.y}%`}
                        priority={index === 0}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Navigation arrows (minimalist style) */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute start-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-brand-text-black rounded-full w-10 h-10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                aria-label="Previous image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute end-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-brand-text-black rounded-full w-10 h-10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-10 shadow-sm"
                aria-label="Next image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image counter (Mobile only) */}
          {images.length > 1 && (
            <div className="md:hidden absolute top-4 end-4 bg-black/50 backdrop-blur-md rounded px-2 py-1 text-white text-[10px] font-bold z-10">
              {selectedIndex + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Thumbnail Navigation (1:1 Square matching competitor) */}
        {images.length > 1 && (
          <div className="flex justify-center">
            <div className="flex items-center gap-4 max-w-full">
              {/* Previous Button */}
              <button
                onClick={goToPrevious}
                className="hidden md:flex flex-shrink-0 w-8 h-8 items-center justify-center rounded-full bg-white border border-gray-100 hover:border-brand-accent-gold transition-colors disabled:opacity-30"
                disabled={images.length <= 1}
              >
                <svg className="w-4 h-4 text-brand-text-main rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Thumbnails */}
              <div
                className="overflow-hidden -my-2 -mx-1"
                ref={emblaThumbnailRef}
                style={{
                  width: `${Math.min(images.length, 5) * 58}px`
                }}
              >
                <div className="flex">
                  {images.map((image, index) => (
                    <div key={index} className="flex-shrink-0 px-1 py-2">
                      <button
                        onClick={() => onThumbnailClick(index)}
                        className={cn(
                          "relative block w-[50px] h-[50px] rounded-md overflow-hidden transition-all duration-300",
                          selectedIndex === index
                            ? "ring-2 ring-brand-accent-gold ring-offset-2 scale-100"
                            : "opacity-60 hover:opacity-100 border border-transparent"
                        )}
                      >
                        <OptimizedImage
                          image={image}
                          size="thumbnail"
                          alt={`${productName} thumbnail ${index + 1}`}
                          className="object-cover absolute inset-0 w-full h-full"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next Button */}
              <button
                onClick={goToNext}
                className="hidden md:flex flex-shrink-0 w-8 h-8 items-center justify-center rounded-full bg-white border border-gray-100 hover:border-brand-accent-gold transition-colors disabled:opacity-30"
                disabled={images.length <= 1}
              >
                <svg className="w-4 h-4 text-brand-text-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Full-Screen Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setIsModalOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-4 end-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white text-sm z-10">
            {modalIndex + 1} / {images.length}
          </div>

          {/* Previous Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToPreviousModal()
              }}
              className="absolute start-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-4 transition-colors z-10"
              aria-label="Previous image"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Main Image Carousel */}
          <div
            className="relative w-full h-full flex items-center justify-center p-4 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden w-full h-full" ref={emblaModalRef}>
              <div className="flex h-full">
                {images.map((image, index) => (
                  <div key={index} className="flex-[0_0_100%] min-w-0 h-full">
                    <div className="relative w-full h-full">
                      <OptimizedImage
                        image={image}
                        size="xlarge"
                        alt={image.altText || `${productName} ${index + 1}`}
                        sizes="(max-width: 768px) 100vw, 80vw"
                        className="object-contain absolute inset-0 w-full h-full"
                        objectPosition={
                          image.cropFocalPoint
                            ? `${image.cropFocalPoint.x}% ${image.cropFocalPoint.y}%`
                            : "center"
                        }
                        priority={index === modalIndex}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Next Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToNextModal()
              }}
              className="absolute end-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-4 transition-colors z-10"
              aria-label="Next image"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-4xl z-10 -my-2 -mx-1">
              <div className="flex overflow-x-auto scrollbar-hide px-3">
                {images.map((image, index) => (
                  <div key={index} className="flex-shrink-0 px-1 py-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        emblaModalApi?.scrollTo(index)
                      }}
                      className={cn(
                        "relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all",
                        modalIndex === index
                          ? "border-white ring-2 ring-white ring-offset-2 ring-offset-black/50"
                          : "border-white/30 hover:border-white/60"
                      )}
                    >
                      <OptimizedImage
                        image={image}
                        size="thumbnail"
                        alt={image.altText || `${productName} ${index + 1}`}
                        className="object-cover absolute inset-0 w-full h-full"
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}
